import { useState } from 'react';
import { ProfileTabs } from './ProfileTabs';
import { PredicateEditor } from './PredicateEditor';
import { StageRail } from './StageRail';
import { StagePanel } from './StagePanelRegistry';
import { useFormConfigStore } from '../state/store';
import type { CanonicalStage, ProfilePredicateClause, StageConfig } from '@capture/shared';
import { formatStageLabel } from '@capture/shared';
import './PipelineTab.css';

export function PipelineTab() {
  const content = useFormConfigStore((s) => s.content);
  const addProfile = useFormConfigStore((s) => s.addProfile);
  const updateProfile = useFormConfigStore((s) => s.updateProfile);

  const profiles = content?.profiles ?? [];

  const [activeId, setActiveId] = useState<string>(() => profiles[0]?.id ?? '');
  const [selectedStage, setSelectedStage] = useState<CanonicalStage | null>(null);

  const activeProfile = profiles.find((p) => p.id === activeId) ?? profiles[0] ?? null;

  if (!activeProfile) {
    return (
      <div className="pipeline-empty">
        <div className="pipeline-empty__title">No pipeline profiles yet</div>
        <div className="pipeline-empty__hint">
          Profiles let you run different pipeline stages for different documents in the same
          job type — for example, invoices may need line-item verification while receipts
          skip straight to export. Start with a fallback profile that covers everything.
        </div>
        <button
          type="button"
          className="pipeline-empty__cta"
          onClick={() => { addProfile(); }}
        >
          + Add first profile
        </button>
      </div>
    );
  }

  function handleClausesChange(next: ProfilePredicateClause[]) {
    if (!activeProfile) return;
    updateProfile(activeProfile.id, {
      predicate: { ...activeProfile.predicate, clauses: next },
    });
  }

  function handleStageToggle(stage: CanonicalStage, nextEnabled: boolean) {
    if (!activeProfile) return;
    updateProfile(activeProfile.id, {
      stages: activeProfile.stages.map((sc) =>
        sc.stage === stage ? { ...sc, enabled: nextEnabled } : sc,
      ),
    });
  }

  function handleStageConfigChange(cfg: StageConfig) {
    if (!activeProfile) return;
    updateProfile(activeProfile.id, {
      stages: activeProfile.stages.map((sc) =>
        sc.stage === cfg.stage ? cfg : sc,
      ),
    });
  }

  const selectedStageConfig = selectedStage
    ? activeProfile.stages.find((sc) => sc.stage === selectedStage) ?? null
    : null;

  return (
    <div className="pipeline-tab">
      <section className="pipeline-section">
        <header className="pipeline-section__heading">
          <h2 className="pipeline-section__title">Profiles</h2>
        </header>
        <p className="pipeline-section__hint">
          Profiles let you branch the pipeline based on the inbound batch header.
          Each profile has match conditions evaluated against the header; the first matching profile applies.
          The Default (fallback) profile runs when no other profile matches.
        </p>
        <ProfileTabs
          profiles={profiles}
          activeId={activeProfile.id}
          onSelect={setActiveId}
          onAdd={addProfile}
        />
      </section>

      {!activeProfile.isFallback && (
        <section className="pipeline-section">
          <header className="pipeline-section__heading">
            <h2 className="pipeline-section__title">Match conditions</h2>
          </header>
          <p className="pipeline-section__hint">
            All clauses must match for this profile to apply. Clauses are evaluated in order against the inbound batch header.
          </p>
          <PredicateEditor
            clauses={activeProfile.predicate.clauses}
            onChange={handleClausesChange}
          />
        </section>
      )}

      <section className="pipeline-section">
        <header className="pipeline-section__heading">
          <h2 className="pipeline-section__title">Pipeline stages</h2>
        </header>
        <p className="pipeline-section__hint">
          Required stages always run. Toggle optional stages on or off. Click a stage to configure it below.
        </p>
        <StageRail
          profile={activeProfile}
          selectedStage={selectedStage}
          onSelect={setSelectedStage}
          onToggle={handleStageToggle}
        />
      </section>

      {selectedStage && selectedStageConfig && (
        <section className="pipeline-section">
          <header className="pipeline-section__heading">
            <h2 className="pipeline-section__title">{formatStageLabel(selectedStage)} settings</h2>
          </header>
          <StagePanel
            stage={selectedStage}
            config={selectedStageConfig}
            onChange={handleStageConfigChange}
          />
        </section>
      )}
    </div>
  );
}

